import { useCallback, useMemo, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { FF_NORMALIZE_IMPORTED_MODEL_NAMES } from '@/consts/featureFlags';
import { useActivePromotions } from '@/hooks/useActivePromotions';
import { usePrecedingIterators } from '@/hooks/usePrecedingIterators';
import { useSeedance25DraftAvailability } from '@/queries/useSeedance25DraftAvailability';
import { I18N_KEYS } from '@/language/keys';
import { useModelPricesMaps, useUserWorkflowRole } from '@/state/workflow.state';
import { ModelAssetToUploadedAsset } from '@/types/api/assets';
import { useNodeData } from '@/components/Recipe/FlowContext';
import { useFlowRunContext } from '@/components/Recipe/RunFlow/FlowRunContext';
import { useGetActiveBatchesWithActiveNode } from '@/components/Recipe/RunFlow/batches.store';
import { getModelPriceInfo, RELEVANT_PRICING_MODEL_PARAMS } from '../Utils';
import {
  SEEDANCE_25_ENHANCE_ASSET_ID_PARAM,
  SEEDANCE_25_ENHANCE_ASSET_URL_PARAM,
  SEEDANCE_25_ENHANCE_RESOLUTION,
  SEEDANCE_25_MODELS,
} from './consts/seedance-25.const';
import { trackSeedance25Enhance, useSeedance25EnhancingAssetId } from './seedance25Enhance.store';
import type { AiModelData } from '@/types/nodes/model';
import type { MediaAsset, UploadedAsset } from '@/types/api/assets';

/**
 * Whether a result is a Seedance 2.5 draft that can be enhanced. Results keep the input they ran
 * with as strings, so a draft is one that ran with `draft` on and is not itself an Enhance run.
 * Fal's seven-day lifetime is checked by the server, which knows when the draft ran.
 */
export function isSeedance25DraftResult(
  modelName: string | undefined,
  asset: UploadedAsset | undefined,
): asset is MediaAsset & UploadedAsset {
  return (
    modelName !== undefined &&
    SEEDANCE_25_MODELS.has(modelName) &&
    asset?.type === 'video' &&
    'id' in asset &&
    Boolean(asset.id) &&
    asset.input?.draft === 'true' &&
    !asset.input?.[SEEDANCE_25_ENHANCE_ASSET_ID_PARAM]
  );
}

/**
 * The params an Enhance run is priced from: the draft's own pricing inputs (its duration, not the
 * node's current one) at 1080p. Mirrors `resolveSeedance25EnhancePricing` in core.
 */
export function getSeedance25EnhancePricingParams(draftInput: Record<string, unknown>): AiModelData['params'] {
  const params: AiModelData['params'] = {};
  for (const key of RELEVANT_PRICING_MODEL_PARAMS) {
    const value = draftInput[key];
    if (value !== undefined) {
      params[key] = value === 'true' ? true : value === 'false' ? false : value;
    }
  }
  return { ...params, draft: false, resolution: SEEDANCE_25_ENHANCE_RESOLUTION };
}

/** The result a model node currently shows (its selected generation), as an uploaded asset. */
export function useSelectedModelResult(nodeId: string): UploadedAsset | undefined {
  const { generations, selectedIndex } = useNodeData<AiModelData>(nodeId);
  const generation = generations?.[selectedIndex ?? 0];
  return useMemo(() => (generation ? ModelAssetToUploadedAsset(generation) : undefined), [generation]);
}

/**
 * Enhance for a Seedance 2.5 node's draft result: renders that draft at 1080p on the same node.
 * Returns what the chip, footer button and properties pane need: whether the result is a draft and
 * can be enhanced, its price and tooltip, the running state, and a click handler.
 */
export function useSeedance25Enhance(
  nodeId: string,
  asset: UploadedAsset | undefined,
  onError?: (error: string) => void,
) {
  const { t } = useTranslation();
  const role = useUserWorkflowRole();
  const model = useNodeData<AiModelData>(nodeId).model;
  const modelPricesMaps = useModelPricesMaps();
  const promotions = useActivePromotions();
  const normalizeImportedModelNames = useFeatureFlagEnabled(FF_NORMALIZE_IMPORTED_MODEL_NAMES);
  const { runNode, initializingBatchNodes } = useFlowRunContext();
  const activeBatches = useGetActiveBatchesWithActiveNode(nodeId);
  const enhancingAssetId = useSeedance25EnhancingAssetId(nodeId);
  // Iterators would expand one Enhance into a paid 1080p render of the same draft per value.
  const hasIterators = Boolean(usePrecedingIterators(nodeId)?.length);

  const draft = isSeedance25DraftResult(model?.name, asset) ? asset : undefined;
  // Older than seven days or from another workspace: the run would be rejected, so say so up front.
  const { isUnavailable } = useSeedance25DraftAvailability(draft);
  const isRunning =
    activeBatches.length > 0 || Object.values(initializingBatchNodes).some((nodeIds) => nodeIds.includes(nodeId));

  const credits = useMemo(() => {
    if (!draft || !model) {
      return undefined;
    }
    return getModelPriceInfo(model, modelPricesMaps, getSeedance25EnhancePricingParams(draft.input ?? {}), {
      normalizeImportedModelNames,
      promotions,
      isVideoOutput: true,
    })?.credits;
  }, [draft, model, modelPricesMaps, normalizeImportedModelNames, promotions]);

  const onClick = useCallback(
    (e?: MouseEvent) => {
      e?.stopPropagation();
      if (!draft || hasIterators || isUnavailable) {
        return;
      }
      trackSeedance25Enhance(nodeId, draft.id, () =>
        runNode(
          nodeId,
          1,
          undefined,
          { credits, surface: 'node_result' },
          {
            paramOverrides: {
              [nodeId]: {
                draft: false,
                resolution: SEEDANCE_25_ENHANCE_RESOLUTION,
                [SEEDANCE_25_ENHANCE_ASSET_ID_PARAM]: draft.id,
                [SEEDANCE_25_ENHANCE_ASSET_URL_PARAM]: draft.url,
              },
            },
          },
        ),
      ).catch((error: unknown) =>
        onError?.(error instanceof Error ? error.message : t(I18N_KEYS.GENERAL.UNKNOWN_ERROR)),
      );
    },
    [credits, draft, hasIterators, isUnavailable, nodeId, onError, runNode, t],
  );

  const tooltip =
    typeof credits === 'number'
      ? t(I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.SEEDANCE_ENHANCE.TOOLTIP_WITH_CREDITS, { credits })
      : t(I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.SEEDANCE_ENHANCE.TOOLTIP);

  return {
    /** The result is a Seedance 2.5 draft (labelled Draft for everyone). */
    isDraft: draft !== undefined,
    /** A draft this user can enhance. */
    canEnhance: draft !== undefined && role === 'editor' && !hasIterators && !isUnavailable,
    /** Iterators feed the node, so Enhance is unavailable even for a draft. */
    hasIterators,
    /** The server can't render this draft in high res any more: it's expired or from another workspace. */
    isUnavailable,
    isRunning,
    isEnhancingThis: draft !== undefined && enhancingAssetId === draft.id,
    tooltip,
    onClick,
  };
}
